import { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => ReactNode;
  width?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  loading?: boolean;
  emptyMessage?: string;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  actions?: (item: T) => ReactNode;
}

export function DataTable<T extends object>({
  columns,
  data,
  keyField,
  loading = false,
  emptyMessage = 'No records found.',
  onEdit,
  onDelete,
  actions,
}: DataTableProps<T>) {
  const hasActions = !!(onEdit || onDelete || actions);

  if (loading) {
    return (
      <div className="data-table__loading" role="status" aria-label="Loading data">
        <div className="data-table__spinner" />
        <span>Loading...</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="data-table__empty" role="status">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="data-table__wrapper">
      <table className="data-table" role="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
            {hasActions && <th style={{ width: '120px' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={String(item[keyField])}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.render
                    ? col.render(item)
                    : String((item as Record<string, unknown>)[col.key] ?? '')}
                </td>
              ))}
              {hasActions && (
                <td className="data-table__actions">
                  {actions ? (
                    actions(item)
                  ) : (
                    <>
                      {onEdit && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => onEdit(item)}
                          aria-label={`Edit ${String(item[keyField])}`}
                        >
                          Edit
                        </button>
                      )}
                      {onDelete && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => onDelete(item)}
                          aria-label={`Delete ${String(item[keyField])}`}
                        >
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
